import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { ordersAPI, usersAPI, storesAPI } from '../lib/api';
import {
  Zap,
  Package,
  CheckCircle,
  Clock,
  RefreshCw,
  Download,
  Search,
  Wifi,
  ChevronRight,
  User,
  X,
} from 'lucide-react';
import { toast } from 'react-hot-toast';

interface Order {
  id: string;
  _id: string;
  orderId?: string;
  orderNumber?: string;
  user?: { name: string; email: string };
  userId?: { name: string; email: string };
  items: Array<{ name: string; quantity: number; price: number }>;
  totalAmount: number;
  status: string;
  storeId?: string;
  deliveryAddress?: {
    fullAddress: string;
    contactName: string;
    contactPhone: string;
    city: string;
    state: string;
    pincode: string;
  };
  deliveryPerson?: {
    fullName: string;
    phone: string;
    email: string;
  };
  createdAt: string;
  feedback?: {
    rating: number;
    comment: string;
  };
}

interface OrderStats {
  newToday: number;
  processing: number;
  deliveredToday: number;
  avgProcessing: string;
  total: number;
  pending: number;
  inProgress: number;
  delivered: number;
  cancelled: number;
}

const Orders = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [filteredOrders, setFilteredOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState<OrderStats>({
    newToday: 0,
    processing: 0,
    deliveredToday: 0,
    avgProcessing: '~16 min',
    total: 0,
    pending: 0,
    inProgress: 0,
    delivered: 0,
    cancelled: 0,
  });

  const [user, setUser] = useState<any>(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
    fetchOrders();
  }, []);

  const handleStatusUpdate = async (orderId: string, status: string, note: string) => {
    try {
      await ordersAPI.updateStatus(orderId, status, note);
      toast.success(`Order ${status === 'preparing' ? 'accepted' : 'rejected'} successfully`);
      fetchOrders();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update order status');
    }
  };

  const [otpModal, setOtpModal] = useState<{ isOpen: boolean; orderId: string; otp: string }>({
    isOpen: false,
    orderId: '',
    otp: '',
  });

  const [assignModal, setAssignModal] = useState<{ isOpen: boolean; orderId: string; storeId?: string }>({
    isOpen: false,
    orderId: '',
    storeId: '',
  });

  const [detailsModal, setDetailsModal] = useState<{ isOpen: boolean; order: Order | any }>({
    isOpen: false,
    order: null,
  });

  const [refundModal, setRefundModal] = useState<{
    isOpen: boolean;
    orderId: string;
    totalAmount: number;
    amount: string;
    reason: string;
  }>({
    isOpen: false,
    orderId: '',
    totalAmount: 0,
    amount: '',
    reason: ''
  });

  const [feedbackModal, setFeedbackModal] = useState<{
    isOpen: boolean;
    orderId: string;
    rating: number;
    comment: string;
  }>({
    isOpen: false,
    orderId: '',
    rating: 5,
    comment: ''
  });

  const handleRefund = async () => {
    try {
      const amount = parseFloat(refundModal.amount);
      if (isNaN(amount) || amount <= 0) {
        toast.error('Please enter a valid amount');
        return;
      }
      if (amount > refundModal.totalAmount) {
        toast.error('Refund amount cannot exceed total amount');
        return;
      }
      await ordersAPI.refund(refundModal.orderId, amount, refundModal.reason);
      toast.success('Refund processed successfully');
      setRefundModal({ isOpen: false, orderId: '', totalAmount: 0, amount: '', reason: '' });
      fetchOrders();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to process refund');
    }
  };

  const [deliveryAgents, setDeliveryAgents] = useState<any[]>([]);

  const fetchDeliveryAgents = async (storeId?: string) => {
    try {
      if (storeId) {
        // Try getting store location first to find nearby agents
        try {
          const storeRes = await storesAPI.getOne(storeId);
          const store = storeRes.data;
          // Check if valid coordinates exist
          if (store?.address?.location?.coordinates?.length === 2) {
            const [lng, lat] = store.address.location.coordinates;
            // Ensure valid numbers
            if (lat !== 0 || lng !== 0) {
              const nearbyRes = await usersAPI.getNearbyDeliveryAgents(lat, lng, 10000); // 10km radius
              if (nearbyRes.data && nearbyRes.data.length > 0) {
                setDeliveryAgents(nearbyRes.data);
                return;
              }
            }
          }
        } catch (e) {
          console.warn("Could not fetch store location or nearby agents, falling back to all agents");
        }
      }

      const response = await usersAPI.getAll({ role: 'delivery', storeId });
      setDeliveryAgents(response.data);
    } catch (error) {
      console.error('Failed to fetch delivery agents:', error);
    }
  };

  useEffect(() => {
    if (assignModal.isOpen) {
      fetchDeliveryAgents(assignModal.storeId);
    }
  }, [assignModal.isOpen, assignModal.storeId]);

  const handleAssignAgent = async (deliveryAgentId: string) => {
    try {
      await ordersAPI.assign(assignModal.orderId, deliveryAgentId);
      toast.success('Agent assigned successfully');
      setAssignModal({ isOpen: false, orderId: '', storeId: '' });
      fetchOrders();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to assign agent');
    }
  };

  const handleAcceptAssignment = async (orderId: string) => {
    try {
      await ordersAPI.acceptAssignment(orderId);
      toast.success('Assignment accepted');
      fetchOrders();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to accept assignment');
    }
  };

  const handleRejectAssignment = async (orderId: string) => {
    try {
      await ordersAPI.rejectAssignment(orderId);
      toast.success('Assignment rejected');
      fetchOrders();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to reject assignment');
    }
  };

  const handleVerifyOTP = async () => {
    try {
      const response = await ordersAPI.verifyOTP(otpModal.orderId, otpModal.otp);
      if (response.data.isValid) {
        toast.success('Order delivered successfully!');
        setOtpModal({ isOpen: false, orderId: '', otp: '' });
        fetchOrders();
      } else {
        toast.error('Invalid OTP');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to verify OTP');
    }
  };

  const handleFeedback = async () => {
    if (!feedbackModal.comment.trim()) {
      toast.error('Please provide a comment');
      return;
    }

    try {
      await ordersAPI.submitFeedback(feedbackModal.orderId, {
        rating: feedbackModal.rating,
        comment: feedbackModal.comment
      });
      toast.success('Thank you for your feedback!');
      setFeedbackModal({ ...feedbackModal, isOpen: false });
      fetchOrders();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Feedback submission failed');
    }
  };

  useEffect(() => {
    filterOrders();
  }, [orders, activeFilter, searchTerm]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const isDelivery = JSON.parse(localStorage.getItem('user') || '{}').role === 'delivery';
      const response = await (isDelivery ? ordersAPI.getMyDeliveries() : ordersAPI.getAll());
      const ordersData = response.data || [];
      setOrders(ordersData);

      // Calculate stats
      const today = new Date().toDateString();
      const todayOrders = ordersData.filter(
        (o: Order) => new Date(o.createdAt).toDateString() === today
      );

      setStats({
        newToday: todayOrders.length,
        processing: ordersData.filter((o: Order) => ['preparing', 'ready_for_pickup'].includes(o.status)).length,
        deliveredToday: todayOrders.filter((o: Order) => o.status === 'delivered').length,
        avgProcessing: '~16 min',
        total: ordersData.length,
        pending: ordersData.filter((o: Order) => o.status === 'pending').length,
        inProgress: ordersData.filter((o: Order) => ['confirmed', 'preparing', 'ready_for_pickup', 'assigned', 'out_for_delivery'].includes(o.status)).length,
        delivered: ordersData.filter((o: Order) => o.status === 'delivered').length,
        cancelled: ordersData.filter((o: Order) => o.status === 'cancelled').length,
      });
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const filterOrders = () => {
    let filtered = orders;

    if (activeFilter !== 'all') {
      const statusMap: Record<string, string[]> = {
        pending: ['pending'],
        inProgress: ['confirmed', 'preparing', 'ready_for_pickup', 'assigned', 'out_for_delivery'],
        delivered: ['delivered'],
        cancelled: ['cancelled'],
      };
      filtered = orders.filter((o) => statusMap[activeFilter]?.includes(o.status));
    }

    if (searchTerm) {
      filtered = filtered.filter(
        (o) =>
          (o.id || o._id || o.orderId || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
          (o.user?.name || o.userId?.name || '').toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredOrders(filtered);
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      pending: 'bg-yellow-100 text-yellow-700',
      confirmed: 'bg-blue-100 text-blue-700',
      preparing: 'bg-purple-100 text-purple-700',
      ready_for_pickup: 'bg-indigo-100 text-indigo-700',
      assigned: 'bg-cyan-100 text-cyan-700',
      out_for_delivery: 'bg-orange-100 text-orange-700',
      delivered: 'bg-green-100 text-green-700',
      cancelled: 'bg-red-100 text-red-700',
      refunded: 'bg-orange-100 text-orange-700 font-bold',
      partially_refunded: 'bg-orange-50 text-orange-600 font-bold',
    };
    return styles[status] || 'bg-gray-100 text-gray-700';
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStoreId = (order: Order) => {
    if (!order.storeId) return '';
    return typeof order.storeId === 'object' ? (order.storeId as any)._id : order.storeId;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#10b981]"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">Orders Management</h1>
            <p className="text-gray-500 mt-1 flex items-center gap-2">
              Track and manage all customer orders in real-time
              <span className="w-1.5 h-1.5 bg-[#10b981] rounded-full animate-pulse"></span>
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-4 py-2 bg-green-50 text-[#10b981] rounded-xl font-bold text-sm border border-green-100">
              <Wifi size={16} />
              Live Updates
            </div>
            <button onClick={fetchOrders} className="p-2.5 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-all shadow-sm text-gray-600">
              <RefreshCw size={20} />
            </button>
            <button className="flex items-center gap-2 px-5 py-2.5 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-all shadow-sm font-bold text-gray-700">
              <Download size={18} />
              Export
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="responsive-stats-grid">
          <div className="bg-gradient-to-br from-orange-400 to-orange-500 rounded-3xl p-5 text-white shadow-lg shadow-orange-500/20 relative overflow-hidden group">
            <div className="absolute -right-4 -top-4 w-24 h-24 bg-white/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-500" />
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                <Zap size={20} />
              </div>
              <span className="text-[10px] font-bold uppercase tracking-widest bg-white/20 px-2 py-1 rounded-lg">Today</span>
            </div>
            <p className="text-3xl font-black">{stats.newToday}</p>
            <p className="text-xs font-bold text-white/70 uppercase tracking-wider mt-1">New Orders</p>
          </div>

          <div className="bg-gradient-to-br from-blue-400 to-blue-500 rounded-3xl p-5 text-white shadow-lg shadow-blue-500/20 relative overflow-hidden group">
            <div className="absolute -right-4 -top-4 w-24 h-24 bg-white/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-500" />
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                <Package size={20} />
              </div>
              <span className="text-[10px] font-bold uppercase tracking-widest bg-white/20 px-2 py-1 rounded-lg">Live</span>
            </div>
            <p className="text-3xl font-black">{stats.processing}</p>
            <p className="text-xs font-bold text-white/70 uppercase tracking-wider mt-1">Processing</p>
          </div>

          <div className="bg-gradient-to-br from-emerald-400 to-emerald-500 rounded-3xl p-5 text-white shadow-lg shadow-emerald-500/20 relative overflow-hidden group">
            <div className="absolute -right-4 -top-4 w-24 h-24 bg-white/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-500" />
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                <CheckCircle size={20} />
              </div>
              <span className="text-[10px] font-bold uppercase tracking-widest bg-white/20 px-2 py-1 rounded-lg">Success</span>
            </div>
            <p className="text-3xl font-black">{stats.deliveredToday}</p>
            <p className="text-xs font-bold text-white/70 uppercase tracking-wider mt-1">Delivered Today</p>
          </div>

          <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-3xl p-5 text-white shadow-lg shadow-purple-500/20 relative overflow-hidden group">
            <div className="absolute -right-4 -top-4 w-24 h-24 bg-white/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-500" />
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                <Clock size={20} />
              </div>
              <span className="text-[10px] font-bold uppercase tracking-widest bg-white/20 px-2 py-1 rounded-lg">Speed</span>
            </div>
            <p className="text-3xl font-black">{stats.avgProcessing}</p>
            <p className="text-xs font-bold text-white/70 uppercase tracking-wider mt-1">Avg. Time</p>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search by order ID, customer name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3.5 bg-white border border-gray-200 rounded-2xl focus:ring-2 focus:ring-[#10b981] focus:border-transparent outline-none transition-all shadow-sm"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto pb-2 lg:pb-0 no-scrollbar">
            {[
              { key: 'all', label: 'All', count: stats.total },
              { key: 'pending', label: 'Pending', count: stats.pending },
              { key: 'inProgress', label: 'In Progress', count: stats.inProgress },
              { key: 'delivered', label: 'Delivered', count: stats.delivered },
              { key: 'cancelled', label: 'Cancelled', count: stats.cancelled },
            ].map((filter) => (
              <button
                key={filter.key}
                onClick={() => setActiveFilter(filter.key)}
                className={`px-6 py-3 rounded-2xl font-bold text-sm transition-all whitespace-nowrap ${activeFilter === filter.key
                  ? 'bg-[#1e3a5f] text-white shadow-lg shadow-blue-900/20'
                  : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                  }`}
              >
                {filter.label} <span className={`ml-1.5 ${activeFilter === filter.key ? 'text-white/60' : 'text-gray-400'}`}>{filter.count}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Orders Table */}
        <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50/50 border-b border-gray-100">
                <tr>
                  <th className="text-left px-6 py-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Order ID</th>
                  <th className="text-left px-6 py-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Date & Time</th>
                  <th className="text-left px-6 py-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Customer</th>
                  <th className="text-left px-6 py-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Items</th>
                  <th className="text-left px-6 py-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Amount</th>
                  <th className="text-left px-6 py-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Status</th>
                  {user?.role !== 'customer' && (
                    <th className="text-right px-6 py-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filteredOrders.length > 0 ? (
                  filteredOrders.map((order) => (
                    <tr key={order.id || order._id} className="hover:bg-gray-50/50 transition-colors group">
                      <td className="px-6 py-5">
                        <span className="font-black text-[#10b981] bg-green-50 px-2.5 py-1 rounded-lg text-xs">
                          #{order.orderId || (order.id || order._id).slice(-6).toUpperCase()}
                        </span>
                      </td>
                      <td className="px-6 py-5">
                        <p className="text-sm font-bold text-gray-900">{formatDate(order.createdAt).split(',')[0]}</p>
                        <p className="text-[10px] font-bold text-gray-400 uppercase mt-0.5">{formatDate(order.createdAt).split(',')[1]}</p>
                      </td>
                      <td className="px-6 py-5">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center text-gray-400 shrink-0">
                            <User size={16} />
                          </div>
                          <div className="min-w-0">
                            <p className="font-bold text-gray-900 truncate">{order.user?.name || order.userId?.name || 'Customer'}</p>
                            <p className="text-[10px] font-bold text-gray-400 truncate uppercase">{order.user?.email || order.userId?.email || ''}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-5">
                        <span className="text-sm font-bold text-gray-600 bg-gray-100 px-2.5 py-1 rounded-lg">
                          {order.items?.length || 0} items
                        </span>
                      </td>
                      <td className="px-6 py-5">
                        <p className="font-black text-gray-900">₹{order.totalAmount.toLocaleString()}</p>
                      </td>
                      <td className="px-6 py-5">
                        <span className={`px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest ${getStatusBadge(order.status)}`}>
                          {order.status.replace('_', ' ')}
                        </span>
                      </td>
                      {user?.role !== 'customer' && (
                        <td className="px-6 py-5 text-right">
                          <div className="flex items-center justify-end gap-2">
                            {order.status === 'pending' && (user?.role === 'super_user' || user?.role === 'entity_admin') && (
                              <>
                                <button
                                  onClick={() => handleStatusUpdate(order.id || order._id, 'preparing', 'Accepted by admin')}
                                  className="px-3 py-1.5 bg-green-500 text-white rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-green-600 transition-all"
                                >
                                  Accept
                                </button>
                                <button
                                  onClick={() => handleStatusUpdate(order.id || order._id, 'cancelled', 'Rejected by admin')}
                                  className="px-3 py-1.5 bg-red-500 text-white rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-red-600 transition-all"
                                >
                                  Reject
                                </button>
                              </>
                            )}
                            {order.status === 'preparing' && (user?.role === 'super_user' || user?.role === 'entity_admin') && (
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleStatusUpdate(order.id || order._id, 'ready_for_pickup', 'Items prepared and ready for pickup')}
                                  className="px-3 py-1.5 bg-indigo-500 text-white rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-indigo-600 transition-all"
                                >
                                  Mark Ready
                                </button>
                                {!order.deliveryPerson && (
                                  <button
                                    onClick={() => setAssignModal({ isOpen: true, orderId: order.id || order._id, storeId: getStoreId(order) })}
                                    className="px-3 py-1.5 bg-blue-500 text-white rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-blue-600 transition-all"
                                  >
                                    Assign Agent
                                  </button>
                                )}
                              </div>
                            )}
                            {order.status === 'ready_for_pickup' && (user?.role === 'super_user' || user?.role === 'entity_admin') && !order.deliveryPerson && (
                              <button
                                onClick={() => setAssignModal({ isOpen: true, orderId: order.id || order._id, storeId: getStoreId(order) })}
                                className="px-3 py-1.5 bg-blue-500 text-white rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-blue-600 transition-all"
                              >
                                Assign Agent
                              </button>
                            )}
                            {order.status === 'assigned' && user?.role === 'delivery' && (
                              <>
                                <button
                                  onClick={() => handleAcceptAssignment(order.id || order._id)}
                                  className="px-3 py-1.5 bg-green-500 text-white rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-green-600 transition-all"
                                >
                                  Accept
                                </button>
                                <button
                                  onClick={() => handleRejectAssignment(order.id || order._id)}
                                  className="px-3 py-1.5 bg-red-500 text-white rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-red-600 transition-all"
                                >
                                  Reject
                                </button>
                              </>
                            )}
                            {order.status === 'ready_for_pickup' && user?.role === 'delivery' && (
                              <button
                                onClick={() => handleStatusUpdate(order.id || order._id, 'out_for_delivery', 'Order picked up by agent')}
                                className="px-3 py-1.5 bg-orange-500 text-white rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-orange-600 transition-all"
                              >
                                Pick Up
                              </button>
                            )}
                            {order.status === 'out_for_delivery' && user?.role === 'delivery' && (
                              <button
                                onClick={() => setOtpModal({ isOpen: true, orderId: order.id || order._id, otp: '' })}
                                className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-green-700 transition-all"
                              >
                                Deliver
                              </button>
                            )}
                            <button
                              onClick={() => setDetailsModal({ isOpen: true, order })}
                              className="p-2 hover:bg-[#10b981] hover:text-white text-gray-400 rounded-xl transition-all"
                            >
                              <ChevronRight size={20} />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={user?.role === 'customer' ? 6 : 7} className="px-6 py-24 text-center">
                      <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Package className="text-gray-200" size={40} />
                      </div>
                      <h3 className="text-lg font-bold text-gray-900">No orders found</h3>
                      <p className="text-sm text-gray-500">Try adjusting your search or filters</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden divide-y divide-gray-50">
            {filteredOrders.length > 0 ? (
              filteredOrders.map((order) => (
                <div key={order.id || order._id} className="p-5 space-y-4 hover:bg-gray-50/50 transition-colors">
                  <div className="flex justify-between items-start">
                    <div className="flex flex-col gap-1">
                      <span className="text-[10px] font-black text-[#10b981] bg-green-50 px-2.5 py-1 rounded-lg w-fit uppercase tracking-widest">
                        #{order.orderId || (order.id || order._id).slice(-6).toUpperCase()}
                      </span>
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">{formatDate(order.createdAt)}</p>
                    </div>
                    <span className={`px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest ${getStatusBadge(order.status)}`}>
                      {order.status.replace('_', ' ')}
                    </span>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gray-100 rounded-2xl flex items-center justify-center text-gray-400 shrink-0">
                      <User size={24} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-gray-900 truncate">{order.user?.name || order.userId?.name || 'Customer'}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <p className="text-[10px] font-bold text-gray-400 uppercase">{order.items?.length || 0} items</p>
                        <span className="w-1 h-1 bg-gray-300 rounded-full" />
                        <p className="text-[10px] font-black text-[#10b981]">₹{order.totalAmount.toLocaleString()}</p>
                      </div>
                    </div>
                    {user?.role !== 'customer' && (
                      <div className="flex items-center gap-2">
                        {order.status === 'pending' && (user?.role === 'super_user' || user?.role === 'entity_admin') && (
                          <div className="flex gap-2 mr-2">
                            <button
                              onClick={() => handleStatusUpdate(order.id || order._id, 'preparing', 'Accepted by admin')}
                              className="px-3 py-1.5 bg-green-500 text-white rounded-lg text-[10px] font-black uppercase tracking-widest"
                            >
                              Accept
                            </button>
                            <button
                              onClick={() => handleStatusUpdate(order.id || order._id, 'cancelled', 'Rejected by admin')}
                              className="px-3 py-1.5 bg-red-500 text-white rounded-lg text-[10px] font-black uppercase tracking-widest"
                            >
                              Reject
                            </button>
                          </div>
                        )}
                        {order.status === 'preparing' && (user?.role === 'super_user' || user?.role === 'entity_admin') && (
                          <div className="flex gap-2 mr-2">
                            <button
                              onClick={() => handleStatusUpdate(order.id || order._id, 'ready_for_pickup', 'Items prepared and ready for pickup')}
                              className="px-3 py-1.5 bg-indigo-500 text-white rounded-lg text-[10px] font-black uppercase tracking-widest"
                            >
                              Mark Ready
                            </button>
                            {!order.deliveryPerson && (
                              <button
                                onClick={() => setAssignModal({ isOpen: true, orderId: order.id || order._id, storeId: getStoreId(order) })}
                                className="px-3 py-1.5 bg-blue-500 text-white rounded-lg text-[10px] font-black uppercase tracking-widest"
                              >
                                Assign
                              </button>
                            )}
                          </div>
                        )}
                        {order.status === 'ready_for_pickup' && (user?.role === 'super_user' || user?.role === 'entity_admin') && !order.deliveryPerson && (
                          <button
                            onClick={() => setAssignModal({ isOpen: true, orderId: order.id || order._id, storeId: getStoreId(order) })}
                            className="px-3 py-1.5 bg-blue-500 text-white rounded-lg text-[10px] font-black uppercase tracking-widest mr-2"
                          >
                            Assign
                          </button>
                        )}
                        {order.status === 'assigned' && user?.role === 'delivery' && (
                          <div className="flex gap-2 mr-2">
                            <button
                              onClick={() => handleAcceptAssignment(order.id || order._id)}
                              className="px-3 py-1.5 bg-green-500 text-white rounded-lg text-[10px] font-black uppercase tracking-widest"
                            >
                              Accept
                            </button>
                            <button
                              onClick={() => handleRejectAssignment(order.id || order._id)}
                              className="px-3 py-1.5 bg-red-500 text-white rounded-lg text-[10px] font-black uppercase tracking-widest"
                            >
                              Reject
                            </button>
                          </div>
                        )}
                        {order.status === 'ready_for_pickup' && user?.role === 'delivery' && (
                          <button
                            onClick={() => handleStatusUpdate(order.id || order._id, 'out_for_delivery', 'Order picked up by agent')}
                            className="px-3 py-1.5 bg-orange-500 text-white rounded-lg text-[10px] font-black uppercase tracking-widest mr-2"
                          >
                            Pick Up
                          </button>
                        )}
                        {order.status === 'out_for_delivery' && user?.role === 'delivery' && (
                          <button
                            onClick={() => setOtpModal({ isOpen: true, orderId: order.id || order._id, otp: '' })}
                            className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-[10px] font-black uppercase tracking-widest mr-2"
                          >
                            Deliver
                          </button>
                        )}
                        <button
                          onClick={() => setDetailsModal({ isOpen: true, order })}
                          className="w-10 h-10 bg-gray-50 text-gray-400 rounded-xl flex items-center justify-center"
                        >
                          <ChevronRight size={20} />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="p-12 text-center">
                <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Package className="text-gray-200" size={32} />
                </div>
                <p className="text-sm font-bold text-gray-900">No orders found</p>
              </div>
            )}
          </div>
        </div>

        {/* OTP Modal */}
        {otpModal.isOpen && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-3xl w-full max-w-md p-8 shadow-2xl">
              <h2 className="text-2xl font-black text-gray-900 mb-2">Verify Delivery</h2>
              <p className="text-gray-500 mb-6">Enter the 6-digit OTP provided by the customer to complete the delivery.</p>

              <input
                type="text"
                maxLength={6}
                placeholder="000000"
                value={otpModal.otp}
                onChange={(e) => setOtpModal({ ...otpModal, otp: e.target.value })}
                className="w-full text-center text-4xl font-black tracking-[1em] py-6 bg-gray-50 border-2 border-gray-100 rounded-2xl focus:ring-4 focus:ring-[#10b981]/20 focus:border-[#10b981] outline-none transition-all mb-8"
              />

              <div className="flex gap-4">
                <button
                  onClick={() => setOtpModal({ isOpen: false, orderId: '', otp: '' })}
                  className="flex-1 py-4 bg-gray-100 text-gray-600 rounded-2xl font-bold hover:bg-gray-200 transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleVerifyOTP}
                  disabled={otpModal.otp.length !== 6}
                  className="flex-1 py-4 bg-[#10b981] text-white rounded-2xl font-bold hover:bg-[#059669] disabled:bg-gray-200 disabled:text-gray-400 transition-all shadow-lg shadow-green-500/20"
                >
                  Verify & Deliver
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Assign Agent Modal */}
        {assignModal.isOpen && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-3xl w-full max-w-md p-8 shadow-2xl">
              <h2 className="text-2xl font-black text-gray-900 mb-2">Assign Delivery Agent</h2>
              <p className="text-gray-500 mb-6">Select an available delivery agent for this order.</p>

              <div className="space-y-3 max-h-[400px] overflow-y-auto mb-8 pr-2">
                {deliveryAgents.length === 0 ? (
                  <p className="text-center py-8 text-gray-400">No delivery agents found.</p>
                ) : (
                  deliveryAgents.map((agent) => (
                    <button
                      key={agent._id}
                      onClick={() => handleAssignAgent(agent._id)}
                      className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-[#10b981]/10 border-2 border-transparent hover:border-[#10b981] rounded-2xl transition-all group"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center text-[#10b981] font-black shadow-sm">
                          {agent.fullName.charAt(0)}
                        </div>
                        <div className="text-left">
                          <p className="font-bold text-gray-900 group-hover:text-[#10b981]">{agent.fullName}</p>
                          <p className="text-xs text-gray-500">{agent.phone || 'No phone'}</p>
                        </div>
                      </div>
                      <ChevronRight size={20} className="text-gray-300 group-hover:text-[#10b981]" />
                    </button>
                  ))
                )}
              </div>

              <button
                onClick={() => setAssignModal({ isOpen: false, orderId: '', storeId: '' })}
                className="w-full py-4 bg-gray-100 text-gray-600 rounded-2xl font-bold hover:bg-gray-200 transition-all"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Order Details Modal */}
        {detailsModal.isOpen && detailsModal.order && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
              <div className="p-8">
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h2 className="text-2xl font-black text-gray-900">Order Details</h2>
                    <p className="text-sm font-bold text-[#10b981] mt-1">
                      #{detailsModal.order.orderId || (detailsModal.order.id || detailsModal.order._id).slice(-6).toUpperCase()}
                    </p>
                  </div>
                  <span className={`px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest ${getStatusBadge(detailsModal.order.status)}`}>
                    {detailsModal.order.status.replace('_', ' ')}
                  </span>
                </div>

                <div className="grid md:grid-cols-2 gap-8">
                  {/* Left Column: Items */}
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-4">Items</h3>
                      <div className="space-y-3">
                        {detailsModal.order.items.map((item: any, idx: number) => (
                          <div key={idx} className="flex justify-between items-center p-3 bg-gray-50 rounded-2xl">
                            <div>
                              <p className="text-sm font-bold text-gray-900">{item.name}</p>
                              <p className="text-[10px] font-bold text-gray-400 uppercase">Qty: {item.quantity}</p>
                            </div>
                            <p className="text-sm font-black text-gray-900">₹{(item.price * item.quantity).toLocaleString()}</p>
                          </div>
                        ))}
                      </div>
                      <div className="mt-4 pt-4 border-t border-dashed border-gray-200 flex justify-between items-center">
                        <p className="text-sm font-bold text-gray-900">Total Amount</p>
                        <p className="text-lg font-black text-[#10b981]">₹{detailsModal.order.totalAmount.toLocaleString()}</p>
                      </div>
                    </div>

                    {/* Delivery Agent (if assigned) */}
                    <div>
                      <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-4">Delivery Agent</h3>
                      {detailsModal.order.deliveryPerson ? (
                        <div className="p-4 bg-blue-50 border border-blue-100 rounded-2xl">
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center text-blue-500 font-black shadow-sm">
                              {detailsModal.order.deliveryPerson.fullName.charAt(0)}
                            </div>
                            <div>
                              <p className="font-bold text-gray-900">{detailsModal.order.deliveryPerson.fullName}</p>
                              <p className="text-xs text-gray-500 font-medium">{detailsModal.order.deliveryPerson.phone}</p>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="p-4 bg-gray-50 border border-gray-100 border-dashed rounded-2xl text-center">
                          <p className="text-xs font-bold text-gray-400 uppercase">No agent assigned yet</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Right Column: Address & Details */}
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-4">Delivery Address</h3>
                      {detailsModal.order.deliveryAddress ? (
                        <div className="p-4 bg-orange-50 border border-orange-100 rounded-2xl space-y-2">
                          <p className="text-sm font-bold text-gray-900">{detailsModal.order.deliveryAddress.contactName}</p>
                          <p className="text-xs text-gray-600 font-medium leading-relaxed">
                            {detailsModal.order.deliveryAddress.fullAddress}, {detailsModal.order.deliveryAddress.city}, {detailsModal.order.deliveryAddress.state} - {detailsModal.order.deliveryAddress.pincode}
                          </p>
                          <p className="text-xs font-black text-orange-600 pt-1">{detailsModal.order.deliveryAddress.contactPhone}</p>
                        </div>
                      ) : (
                        <div className="p-4 bg-gray-50 border border-gray-100 border-dashed rounded-2xl">
                          <p className="text-xs font-bold text-gray-400 uppercase">Address details not available</p>
                        </div>
                      )}
                    </div>

                    <div>
                      <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-4">Customer Details</h3>
                      <div className="p-4 bg-gray-50 rounded-2xl">
                        <p className="text-sm font-bold text-gray-900">{detailsModal.order.user?.name || detailsModal.order.userId?.name || 'Customer'}</p>
                        <p className="text-xs text-gray-500 font-medium">{detailsModal.order.user?.email || detailsModal.order.userId?.email || ''}</p>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-4">Order Date</h3>
                      <p className="text-sm font-bold text-gray-900">{formatDate(detailsModal.order.createdAt)}</p>
                    </div>
                  </div>
                </div>

                <div className="mt-10 flex flex-col sm:flex-row gap-4">
                  {(user?.role === 'super_user' || user?.role === 'entity_admin') && (
                    <button
                      onClick={() => {
                        setRefundModal({
                          isOpen: true,
                          orderId: detailsModal.order?._id || detailsModal.order?.id || '',
                          totalAmount: detailsModal.order?.totalAmount || 0,
                          amount: (detailsModal.order?.totalAmount || 0).toString(),
                          reason: ''
                        });
                        setDetailsModal({ isOpen: false, order: null });
                      }}
                      className="flex-1 py-4 bg-red-50 text-red-600 rounded-2xl font-black uppercase tracking-widest hover:bg-red-100 transition-all border border-red-200"
                    >
                      Issue Refund
                    </button>
                  )}
                  {detailsModal.order?.status === 'delivered' && user?.role === 'customer' && !detailsModal.order?.feedback && (
                    <button
                      onClick={() => {
                        setFeedbackModal({
                          isOpen: true,
                          orderId: detailsModal.order?._id || detailsModal.order?.id || '',
                          rating: 5,
                          comment: ''
                        });
                        setDetailsModal({ isOpen: false, order: null });
                      }}
                      className="flex-1 py-4 bg-blue-50 text-blue-600 rounded-2xl font-black uppercase tracking-widest hover:bg-blue-100 transition-all border border-blue-200"
                    >
                      Rate Order
                    </button>
                  )}
                  <button
                    onClick={() => setDetailsModal({ isOpen: false, order: null })}
                    className="flex-1 py-4 bg-gray-900 text-white rounded-2xl font-black uppercase tracking-widest hover:bg-black transition-all shadow-lg shadow-gray-900/10"
                  >
                    Close Details
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Refund Modal */}
        {refundModal.isOpen && (
          <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
            <div className="bg-white rounded-3xl w-full max-w-md p-8 shadow-2xl">
              <h2 className="text-2xl font-black text-gray-900 mb-2">Issue Refund</h2>
              <p className="text-sm text-gray-500 mb-6">Process a full or partial refund for this order.</p>

              <div className="space-y-4">
                <div>
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5 block">Refund Amount (Max: ₹{refundModal.totalAmount})</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 font-bold text-gray-400">₹</span>
                    <input
                      type="number"
                      value={refundModal.amount}
                      onChange={(e) => setRefundModal({ ...refundModal, amount: e.target.value })}
                      className="w-full pl-8 pr-4 py-3.5 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none transition-all font-bold"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5 block">Reason for Refund</label>
                  <textarea
                    value={refundModal.reason}
                    onChange={(e) => setRefundModal({ ...refundModal, reason: e.target.value })}
                    placeholder="e.g. Items missing, poor quality, etc."
                    rows={3}
                    className="w-full px-4 py-3.5 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none transition-all resize-none"
                  />
                </div>
              </div>

              <div className="flex gap-4 mt-8">
                <button
                  onClick={() => setRefundModal({ ...refundModal, isOpen: false })}
                  className="flex-1 py-4 bg-gray-100 text-gray-600 rounded-2xl font-bold hover:bg-gray-200 transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRefund}
                  className="flex-1 py-4 bg-red-500 text-white rounded-2xl font-black uppercase tracking-widest hover:bg-red-600 transition-all shadow-lg shadow-red-500/20"
                >
                  Process Refund
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Feedback Modal */}
        {feedbackModal.isOpen && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-3xl w-full max-w-lg p-8 shadow-2xl relative">
              <button
                onClick={() => setFeedbackModal({ ...feedbackModal, isOpen: false })}
                className="absolute right-6 top-6 text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
              <h2 className="text-2xl font-black text-gray-900 mb-2">Rate Your Experience</h2>
              <p className="text-gray-500 mb-8 uppercase text-[10px] font-black tracking-widest">Order #{feedbackModal.orderId.slice(-6)}</p>

              <div className="space-y-6">
                <div>
                  <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3">Rating</label>
                  <div className="flex gap-4">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        onClick={() => setFeedbackModal({ ...feedbackModal, rating: star })}
                        className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all ${feedbackModal.rating >= star ? 'bg-yellow-400 text-white shadow-lg shadow-yellow-400/20' : 'bg-gray-50 text-gray-300'
                          }`}
                      >
                        <Zap size={24} fill={feedbackModal.rating >= star ? "currentColor" : "none"} />
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3">Your Comment</label>
                  <textarea
                    value={feedbackModal.comment}
                    onChange={(e) => setFeedbackModal({ ...feedbackModal, comment: e.target.value })}
                    className="w-full px-6 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-blue-500/10 focus:bg-white outline-none min-h-[120px] transition-all text-sm font-bold"
                    placeholder="How was the food and delivery?"
                  />
                </div>

                <button
                  onClick={handleFeedback}
                  className="w-full py-5 bg-blue-600 text-white rounded-2xl font-black uppercase tracking-widest hover:bg-blue-700 transition-all shadow-xl shadow-blue-600/20"
                >
                  Submit Feedback
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Orders;